//	Romanian-locale number/date formatting shared by the chart engine (SSR, islands, admin).
//	Intl instances are cached: a chart formats hundreds of values per layout.

const formatters = new Map();

function numberFormatter(decimals) {
	const key = String(decimals);
	let f = formatters.get(key);
	if (!f) {
		f = new Intl.NumberFormat("ro-RO", {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		});
		formatters.set(key, f);
	}
	return f;
}

export function formatNumber(value, decimals = 0) {
	if (typeof value !== "number" || !Number.isFinite(value)) return "—";
	return numberFormatter(Math.max(0, Math.min(4, decimals | 0))).format(value);
}

//	decimals needed so that the value is not rounded away (max 4): 0.5 → 1, 12 → 0
export function autoDecimals(values) {
	let d = 0;
	for (const v of values) {
		if (typeof v !== "number" || !Number.isFinite(v)) continue;
		for (let k = 0; k <= 4; k++) {
			if (Math.abs(v * 10 ** k - Math.round(v * 10 ** k)) < 1e-9) {
				d = Math.max(d, k);
				break;
			}
			if (k === 4) d = 4;
		}
	}
	return d;
}

const CURRENCY = { RON: { suffix: " lei" }, EUR: { suffix: " €" }, USD: { suffix: " $" }, GBP: { suffix: " £" } };

//	the unit as a short caption ("%", "lei", "mil. lei"), for the axis corner and the table header
export function unitLabel(options = {}) {
	if (options.yFormat === "percent") return "%";
	const parts = [];
	if (options.unit) parts.push(options.unit.trim());
	if (options.yFormat === "currency" && CURRENCY[options.currency]) parts.push(CURRENCY[options.currency].suffix.trim());
	return parts.join(" ");
}

//	full value for tooltips, direct labels and the table: "1.234,5 mil. lei", "12,5%"
export function formatValue(value, options = {}) {
	if (typeof value !== "number" || !Number.isFinite(value)) return "—";
	const decimals = typeof options.decimals === "number" ? options.decimals : 0;
	const base = formatNumber(value, decimals);
	if (options.yFormat === "percent") return base + "%";
	const unit = unitLabel(options);
	return unit ? base + " " + unit : base;
}

function trimmed(value, maxDecimals) {
	const s = numberFormatter(maxDecimals).format(value);
	//	ro-RO uses "," as decimal separator: strip trailing zeros after it
	return s.includes(",") ? s.replace(/,?0+$/, "") : s;
}

//	axis ticks: compact above 10.000 (mii / mil. / mld.), percent keeps its sign, units stay in the corner caption
export function formatTick(value, options = {}) {
	if (typeof value !== "number" || !Number.isFinite(value)) return "";
	const abs = Math.abs(value);
	let text;
	if (options.compactTicks !== false && abs >= 1e9) text = trimmed(value / 1e9, 1) + " mld.";
	else if (options.compactTicks !== false && abs >= 1e6) text = trimmed(value / 1e6, 1) + " mil.";
	else if (options.compactTicks !== false && abs >= 1e4) text = trimmed(value / 1e3, 1) + " mii";
	else text = trimmed(value, Math.min(4, Math.max(options.decimals || 0, decimalsOf(value))));
	if (options.yFormat === "percent") text += "%";
	return text;
}

function decimalsOf(value) {
	for (let k = 0; k <= 4; k++) {
		if (Math.abs(value * 10 ** k - Math.round(value * 10 ** k)) < 1e-9) return k;
	}
	return 4;
}

const MONTHS = new Intl.DateTimeFormat("ro-RO", { month: "short", timeZone: "UTC" });

//	ISO-ish x labels ("2024", "2024-03", "2024-03-15", "15.03.2024") → "2024", "mar. 2024", "15 mar. 2024"
export function formatDateLabel(text, compact = false) {
	if (typeof text !== "string") return text == null ? "" : String(text);
	const s = text.trim();
	let y, m, d;
	let match;
	if ((match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s))) [, y, m, d] = match;
	else if ((match = /^(\d{1,2})[./](\d{1,2})[./](\d{4})$/.exec(s))) [, d, m, y] = match;
	else if ((match = /^(\d{4})-(\d{1,2})$/.exec(s))) [, y, m] = match;
	else if ((match = /^(\d{1,2})[./](\d{4})$/.exec(s))) [, m, y] = match;
	else return s;
	const month = m ? MONTHS.format(new Date(Date.UTC(Number(y), Number(m) - 1, 1))) : "";
	if (d) return compact ? `${Number(d)} ${month}` : `${Number(d)} ${month} ${y}`;
	if (m) return compact ? `${month} ${String(y).slice(2)}` : `${month} ${y}`;
	return y;
}
